import * as React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { IdentifierInput } from '../components/IdentifierInput';
import {
  Aal2CodeForm,
  type Aal2Method,
  type Aal2FieldName,
} from '../components/Aal2CodeForm';
import { useLogin, type LoginValues } from '../hooks/useLogin';
import { getLoginFlow, createLogoutFlow, performLogout } from '@/lib/api/kratos';
import type { LoginFlow } from '@/lib/api/kratos';
import { discoverSso, submitOidcLoginForm } from '@/lib/api/sso';
import { useAuthStore } from '@/store/authStore';
import { useSettingsStore } from '@/store/settingsStore';

// Define AuthMethod type locally since we're not using the MethodSelection component
type AuthMethod = {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
};

// Simple password form component
function PasswordForm({ 
  onSubmit, 
  disabled
}: { 
  onSubmit: (password: string) => void;
  disabled: boolean;
}) {
  const [password, setPassword] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(password);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <div className="text-sm font-medium">Password</div>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={disabled}
          required
          placeholder="Enter your password"
        />
      </div>
      <Button type="submit" className="w-full" disabled={disabled || !password}>
        {disabled ? 'Signing in...' : 'Sign In'}
      </Button>
    </form>
  );
}

// Email-code (passwordless) OTP entry form. Shown once Kratos moves the
// flow to `sent_email` and exposes the `code` text node. The parent owns
// the API call + loading state via `disabled`, and passes Kratos' inline
// error (wrong/expired code) through `error`.
function CodeForm({
  email,
  onSubmit,
  onResend,
  disabled,
  error,
}: {
  email: string;
  onSubmit: (code: string) => void;
  onResend: () => void;
  disabled: boolean;
  error?: string;
}) {
  const [code, setCode] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed) onSubmit(trimmed);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <div className="text-sm font-medium">Sign-in code</div>
        <p className="text-xs text-muted-foreground">
          Enter the code we emailed to{' '}
          <span className="font-medium">{email}</span>.
        </p>
        <Input
          id="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={disabled}
          required
          placeholder="Enter your code"
        />
      </div>
      {error && (
        <div className="p-3 text-sm rounded-md text-destructive bg-destructive/10 border border-destructive/20">
          {error}
        </div>
      )}
      <Button type="submit" className="w-full" disabled={disabled || !code.trim()}>
        {disabled ? 'Verifying...' : 'Sign In'}
      </Button>
      <div className="text-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs"
          onClick={onResend}
          disabled={disabled}
        >
          Resend code
        </Button>
      </div>
    </form>
  );
}

// Declare global functions from Kratos WebAuthn script
declare global {
  interface Window {
    oryPasskeyLogin?: () => void;
    oryPasskeyLoginAutocompleteInit?: () => void;
  }
}

/**
 * LoginPage Component
 * 
 * Handles user authentication using Ory Kratos flows
 * Supports multiple authentication methods and dynamic form rendering
 */
export function LoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const [selectedMethod, setSelectedMethod] = React.useState<string>('password');
  const [step, setStep] = React.useState<'identify' | 'choose_method' | 'authenticate'>('identify');
  const [email, setEmail] = React.useState<string>('');
  const [showGenericError, setShowGenericError] = React.useState<boolean>(false);
  const [showUi, setShowUi] = React.useState<boolean>(false);
  const [checkingSession, setCheckingSession] = React.useState<boolean>(true);
  const [flowExpired, setFlowExpired] = React.useState<boolean>(false);
  // Set immediately before submitting the OIDC form so the user sees a
  // spinner instead of the email form during the redirect chain
  // (dashboard → Kratos → Hydra → Orchestrator → Polis → IdP → ...).
  // Browsers keep painting the current DOM until the next document is
  // ready, so this state stays visible for the full inflight chain.
  const [redirectingToSso, setRedirectingToSso] =
    React.useState<boolean>(false);
  
  const loginMutation = useLogin();
  const { setSession, clearSession } = useAuthStore();
  // Holds the email of the currently authenticated identity (if any).
  // Used by handleIdentifierSubmit to decide between fast-path (same
  // user retypes their own email → redirect to /) vs logout-and-reauth
  // (different user typed → log out current session before initiating
  // the new SSO flow). See bug fix plan in
  // wya-polis-cdk/docs/sso-implementation-plan.md (V1 hotfix).
  const [existingSessionEmail, setExistingSessionEmail] = React.useState<
    string | null
  >(null);

  // Session check on mount.
  //
  // Two access paths land here:
  //   - /auth/login  (no flow param)            ← typical entry, fast-path
  //   - /auth/login?flow=...  (Kratos flow id)  ← explicit re-auth
  //
  // Behavior:
  //   - Active session + no flowId → redirect to '/' (dashboard). Without
  //     this fast-path Kratos's `/self-service/login/browser` sees the
  //     active session and redirects back here, creating an infinite
  //     "checking session → redirecting to login" loop.
  //   - Active session + flowId    → keep the form open so a DIFFERENT
  //     user can authenticate. handleIdentifierSubmit logs out the
  //     existing session if the typed email doesn't match.
  //   - No session                 → continue with the standard Kratos
  //     flow (create one if needed).
  React.useEffect(() => {
    const checkSession = async () => {
      console.log('LoginPage: Checking existing session');
      setCheckingSession(true);

      let hasActiveSession = false;
      let needsAal2StepUp = false;
      const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
      try {
        const response = await fetch(`${kratosUrl}/sessions/whoami`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const sessionData = await response.json();
          if (sessionData.active) {
            hasActiveSession = true;
            setSession(sessionData);
            const email = sessionData?.identity?.traits?.email as
              | string
              | undefined;
            if (email) setExistingSessionEmail(email.toLowerCase().trim());
          }
        } else if (response.status === 403) {
          // 403 here is the documented "session AAL too low" signal:
          // `error.id: session_aal2_required`. The user DOES have a valid
          // AAL1 session — they just need to complete step-up. Treat this
          // case distinctly from "no session": do not redirect to "/" and
          // do not create a fresh aal1 flow on top.
          try {
            const body = await response.json();
            if (body?.error?.id === 'session_aal2_required') {
              needsAal2StepUp = true;
            }
          } catch {
            /* no json — fall through */
          }
        }
      } catch (error) {
        console.log('LoginPage: Session check failed (expected for logged out users):', error);
      }

      // Fast path: active aal2 session and no explicit flow → land in the
      // dashboard. Anyone wanting to log in as someone else must hit
      // /auth/login?flow=... which keeps the form open below.
      if (hasActiveSession && !flowId) {
        console.log('LoginPage: Active session + no flowId — redirecting to /');
        window.location.href = '/';
        return;
      }

      setCheckingSession(false);

      // Half-built AAL1 cookie + step-up needed + no flow → kick directly
      // into the aal2 login flow so Kratos mints a flow with the totp /
      // lookup_secret nodes.
      if (needsAal2StepUp && !flowId) {
        console.log('LoginPage: session_aal2_required — bootstrapping aal2 flow');
        window.location.href = `${kratosUrl}/self-service/login/browser?aal=aal2`;
        return;
      }

      // No session and no flow → ask Kratos to create one.
      if (!flowId) {
        console.log('LoginPage: No flowId found, redirecting to Kratos to create flow');
        window.location.href = `${kratosUrl}/self-service/login/browser`;
        return;
      }

      setShowUi(true);
    };

    checkSession();
  }, [flowId, setSession]);

  // If there's already a session for a DIFFERENT email, terminate it
  // before initiating a new login. Reuses the same Kratos logout helpers
  // the UserMenu calls.
  const ensureCleanSessionFor = async (
    requestedEmail: string,
  ): Promise<'fast_path_same_user' | 'proceed'> => {
    if (!existingSessionEmail) return 'proceed';
    const target = requestedEmail.toLowerCase().trim();
    if (existingSessionEmail === target) return 'fast_path_same_user';

    console.log('LoginPage: existing session belongs to another user — logging out first', {
      existing: existingSessionEmail,
      requested: target,
    });
    try {
      const lf = await createLogoutFlow();
      await performLogout(lf.logout_token);
    } catch (err) {
      console.error('LoginPage: logout before re-auth failed (continuing anyway)', err);
    }
    clearSession();
    useSettingsStore.getState().clearAllContext();
    setExistingSessionEmail(null);
    return 'proceed';
  };

  // Fetch existing login flow (only if we have a flowId)
  const { data: flow, isLoading, error, refetch } = useQuery({
    queryKey: ['loginFlow', flowId],
    queryFn: async (): Promise<LoginFlow> => {
      if (!flowId) {
        throw new Error('No flow ID available');
      }

      try {
        return await getLoginFlow(flowId);
      } catch (err) {
        console.error('Failed to get login flow:', err);

        // Anti-loop guard: if we just bounced through Kratos to mint a
        // brand-new flow id and it ALREADY fails to load, do NOT redirect
        // again. We'd just spin forever (e.g. when the session is in a
        // half-built AAL state and Kratos keeps rejecting the flow). Flag
        // the flow as expired so the render path shows a friendly "Start
        // over" CTA instead of the generic error card.
        const lastBounceFlow = sessionStorage.getItem('loginFlowBounce');
        if (lastBounceFlow === flowId) {
          console.warn('Flow load failed on the just-created flow — surfacing Start over UI.');
          setFlowExpired(true);
          throw err;
        }
        sessionStorage.setItem('loginFlowBounce', flowId);

        const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
        window.location.href = `${kratosUrl}/self-service/login/browser`;
        throw err;
      }
    },
    enabled: !!flowId, // Only run if we have a flowId
    retry: false, // Don't retry, redirect instead
    staleTime: 0, // Always fetch fresh flow data
  });

  // Clear the bounce marker as soon as we successfully load a flow — so the
  // anti-loop guard only blocks consecutive failures, not future legitimate
  // retries.
  React.useEffect(() => {
    if (flow) sessionStorage.removeItem('loginFlowBounce');
  }, [flow]);

  // Load WebAuthn script when flow is available
  React.useEffect(() => {
    if (!flow) return;

    // Check if we have a WebAuthn script node and load it
    const webauthnNode = flow.ui.nodes.find(node => 
      node.type === 'script' && node.group === 'webauthn'
    );

    if (webauthnNode && !document.getElementById('webauthn_script')) {
      console.log('Loading WebAuthn script from flow...');
      const script = document.createElement('script');
      script.id = 'webauthn_script';
      script.src = webauthnNode.attributes.src as string;
      script.async = webauthnNode.attributes.async as boolean;
      script.crossOrigin = webauthnNode.attributes.crossorigin as string;
      if (webauthnNode.attributes.integrity) {
        script.integrity = webauthnNode.attributes.integrity as string;
      }
      if (webauthnNode.attributes.referrerpolicy) {
        script.referrerPolicy = webauthnNode.attributes.referrerpolicy as string;
      }
      if (webauthnNode.attributes.nonce) {
        script.nonce = webauthnNode.attributes.nonce as string;
      }
      
      document.head.appendChild(script);
      console.log('WebAuthn script added to document head');
    }
  }, [flow]);

  // Determine current step based on flow state and handle security errors
  React.useEffect(() => {
    if (!flow) return;

    console.log('Flow state changed:', {
      flowId: flow.id,
      nodes: flow.ui.nodes.map(n => ({ type: n.type, group: n.group, name: n.attributes.name, value: n.attributes.value, messages: n.messages })),
      messages: flow.ui.messages,
    });

    // Debug: Check for error messages
    console.log('Checking for error 4000037...');
    const globalError = flow.ui.messages?.find((msg: any) => msg.id === 4000037);
    const nodeError = flow.ui.nodes?.find((node: any) => 
      node.messages?.some((msg: any) => msg.id === 4000037)
    );
    console.log('Global error 4000037:', globalError);
    console.log('Node error 4000037:', nodeError);

    // Check for account not found error (4000037) for security handling
    // Check both global messages and node-specific messages
    const hasAccountNotFoundError = 
      flow.ui.messages?.some((msg: any) => msg.id === 4000037) ||
      flow.ui.nodes?.some((node: any) => 
        node.messages?.some((msg: any) => msg.id === 4000037)
      );
    
    if (hasAccountNotFoundError) {
      console.log('Account not found error detected - showing generic error for security');
      setShowGenericError(true);
      setStep('identify');
      return; // Don't proceed with normal flow logic
    }

    // Email-code login: once Kratos sends the OTP it moves the flow to
    // `sent_email` and exposes a `code` TEXT node (the OTP entry field —
    // the choose_method state only had a `code` SUBMIT button). Route the
    // user straight to the OTP form instead of falling through to the
    // method-picker logic below, which would otherwise bounce them back to
    // `choose_method` (the "needs two clicks" symptom).
    const codeInputNode = flow.ui.nodes.find(
      (node) =>
        node.attributes.name === 'code' &&
        node.attributes.type !== 'hidden' &&
        node.attributes.type !== 'submit'
    );
    if (codeInputNode) {
      const idNode = flow.ui.nodes.find(
        (node) => node.attributes.name === 'identifier' && node.attributes.value
      );
      if (idNode) setEmail(String(idNode.attributes.value));
      setSelectedMethod('code');
      setStep('authenticate');
      return;
    }

    // Check if we have method buttons (excluding identifier_first)
    const methodButtons = flow.ui.nodes.filter(node =>
      node.type === 'input' &&
      ['button', 'submit'].includes(node.attributes.type as string) &&
      node.attributes.value !== 'identifier_first'
    );

    // Check if we have an identifier field with a value (user has entered email)
    const identifierNode = flow.ui.nodes.find(node =>
      node.attributes.name === 'identifier' &&
      node.attributes.value
    );

    // Detect AAL2 step-up flows. Kratos drops the `identifier_first` node and
    // the `identifier` field entirely (it already knows who the user is from
    // the AAL1 session cookie) — only AAL2 methods are exposed: totp,
    // lookup_secret, webauthn. In that case we MUST skip the identify step
    // or the user would be stuck typing their email forever.
    const isAal2Flow = (flow as any).requested_aal === 'aal2';
    const identifierFirstButton = flow.ui.nodes.find(
      (n) =>
        n.attributes.name === 'method' &&
        n.attributes.value === 'identifier_first'
    );

    if (isAal2Flow && methodButtons.length > 0) {
      // Pull the identity email from anywhere it might be cached so the UI
      // can still greet the user by email if the layout uses it.
      const emailFromTraits =
        ((flow as any).identity?.traits?.email as string | undefined) ?? '';
      if (emailFromTraits) setEmail(emailFromTraits);

      // AAL2 step-up: always skip the method picker and drop the user
      // straight onto the input form for the primary factor. Pick a default
      // by priority (totp > webauthn > lookup_secret) so the most common
      // case — Authenticator app code — appears on first paint.
      //
      // Why no picker: the picker re-renders on every state change, so
      // clicking a method would trigger this effect again with the same
      // multi-group flow and reset the step back to `choose_method` (the
      // classic "needs two clicks to advance" bug). Auto-selecting also
      // matches established UX — Google, GitHub etc. show the primary
      // factor and offer the alternate as a small footer link.
      const uniqueGroups = Array.from(
        new Set(methodButtons.map((n) => n.group)),
      );
      const priority = ['totp', 'webauthn', 'lookup_secret'];
      const defaultMethod =
        priority.find((m) => uniqueGroups.includes(m)) ?? uniqueGroups[0];
      // Honour an in-progress toggle ("use backup code instead") so the
      // user's manual switch sticks across re-renders of the same flow.
      const isToggleableAal2 = uniqueGroups.includes(selectedMethod);
      if (!isToggleableAal2) {
        setSelectedMethod(defaultMethod);
      }
      setStep('authenticate');
    } else if (methodButtons.length > 0 && identifierNode) {
      // Standard identifier_first flow with the email already submitted.
      setStep('choose_method');
      setEmail(String(identifierNode.attributes.value));
    } else if (methodButtons.length > 0 && !identifierFirstButton) {
      // Methods are available but there is no identifier_first gate — this
      // is a non-IF flow (rare in WYA but supported). Show the method picker.
      setStep('choose_method');
    } else if (email && selectedMethod && selectedMethod !== 'password') {
      // User has selected a method, show authentication form
      setStep('authenticate');
    } else {
      // Default to identifier input
      setStep('identify');
    }
  }, [flow, email, selectedMethod]);

  // Extract available authentication methods from flow
  const availableMethods = React.useMemo((): AuthMethod[] => {
    if (!flow || step !== 'choose_method') return [];

    const methods: AuthMethod[] = [];
    
    // Get method buttons (excluding identifier_first)
    const methodButtons = flow.ui.nodes.filter(node => 
      node.type === 'input' && 
      ['button', 'submit'].includes(node.attributes.type as string) &&
      node.attributes.value !== 'identifier_first'
    );

    // Extract unique method groups
    const methodGroups = [...new Set(methodButtons.map(node => node.group))];
    
    console.log('Method groups found:', methodGroups);
    console.log('Method buttons found:', methodButtons);

    methodGroups.forEach(group => {
      if (group === 'password') {
        methods.push({
          id: 'password',
          name: 'Password',
          description: 'Sign in with email and password',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          ),
        });
      } else if (group === 'passkey') {
        methods.push({
          id: 'passkey',
          name: 'Passkey',
          description: 'Sign in with passkey',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 16.5H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          ),
        });
      } else if (group === 'code') {
        methods.push({
          id: 'code',
          name: 'Email Code',
          description: 'Send sign in code to email',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          ),
        });
      } else if (group === 'oidc') {
        // Handle OIDC providers. The platform-internal "wya-sso" provider
        // is policy-driven (auto-redirect when SSO is enabled at the org
        // level), so it must never appear as a manual button — users
        // would land on a misleading dead-end. Filter it out here.
        const oidcNodes = flow.ui.nodes.filter(
          (node) =>
            node.group === 'oidc' &&
            (node.attributes.value as string) !== 'wya-sso',
        );
        oidcNodes.forEach(node => {
          const provider = node.attributes.value as string;
          methods.push({
            id: `oidc_${provider}`,
            name: `${provider.charAt(0).toUpperCase() + provider.slice(1)}`,
            description: `Sign in with ${provider}`,
            icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          });
        });
      } else if (group === 'totp') {
        // AAL2 step-up: user already has TOTP enrolled.
        methods.push({
          id: 'totp',
          name: 'Authenticator app',
          description: 'Enter the 6-digit code from your authenticator',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-1.1.9-2 2-2s2 .9 2 2-2 4-2 4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });
      } else if (group === 'lookup_secret') {
        // AAL2 fallback: user lost their authenticator and falls back to a
        // one-time backup code from the batch they downloaded at setup.
        methods.push({
          id: 'lookup_secret',
          name: 'Backup code',
          description: 'Use one of the one-time codes you saved',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          ),
        });
      }
    });

    console.log('Available methods extracted:', methods);
    return methods;
  }, [flow, step]);

  const handleIdentifierSubmit = async (identifier: string) => {
    if (!flow) return;

    // Same-user fast path: if the active session already belongs to
    // this email, send them straight to the dashboard. Different-user
    // path: log out the existing session BEFORE initiating SSO, so the
    // upstream OIDC flow does not silently reuse it.
    const decision = await ensureCleanSessionFor(identifier);
    if (decision === 'fast_path_same_user') {
      window.location.href = '/';
      return;
    }

    // Step A — SSO discovery. If the email's tenant has SSO enabled, hand
    // the flow off to OIDC immediately by submitting a top-level form to
    // Kratos' action URL with method=oidc + provider=wya-sso +
    // upstream_parameters.login_hint=<loginToken>. Kratos passes that
    // hint to Hydra → SSO Orchestrator, which resolves tenant +
    // connection and proxies through Polis to the customer IdP.
    //
    // If SSO is NOT enabled (or discovery fails / is unconfigured), fall
    // through to the existing Kratos `identifier_first` flow that ends
    // at the method picker.
    try {
      const disco = await discoverSso(identifier);
      if (disco.ssoEnabled && disco.loginToken) {
        console.log('SSO enabled for', identifier, '— handing off to OIDC.');
        // Paint the spinner BEFORE submitting the form. Yielding to a
        // microtask lets React commit the state update and the browser
        // render the new DOM before navigation steals the paint thread.
        setRedirectingToSso(true);
        await Promise.resolve();
        submitOidcLoginForm({
          flowAction: flow.ui.action,
          csrfToken: getCsrfToken(),
          loginToken: disco.loginToken,
        });
        // The browser navigates away. No further state to set.
        return;
      }
      if (disco.enforced) {
        // The tenant requires SSO but discovery did not produce a token —
        // do not let the user fall back to password.
        console.warn('Tenant requires SSO but no loginToken was issued.');
        setShowGenericError(true);
        return;
      }
    } catch (err) {
      console.error('SSO discovery threw, falling back to non-SSO flow:', err);
    }

    // Step B — non-SSO path. Same payload as before: identifier_first
    // submit, then refetch to land on the method picker.
    const payload = {
      method: 'identifier_first',
      identifier: identifier,
      csrf_token: getCsrfToken(),
    };

    console.log('Sending identifier_first payload:', payload);

    try {
      const result = await loginMutation.mutateAsync({
        flow: flow,
        values: payload,
      });

      console.log('Identifier submission result:', result);

      // Store the email for later steps
      setEmail(identifier);

      // Refetch to get the updated flow state
      refetch();
    } catch (error: any) {
      console.log('Identifier submission completed with error response');
      console.log('Error details:', error);

      // Store the email for later steps
      setEmail(identifier);

      // Refetch to get the updated flow state
      // The useEffect will handle the security error detection
      refetch();
    }
  };

  const handleMethodSelect = async (methodId: string) => {
    setSelectedMethod(methodId);
    
    // For methods that can be executed immediately, do so
    if (methodId === 'code') {
      // Ask Kratos to send the sign-in code. The `identifier` is REQUIRED
      // here — in an identifier_first flow the `identifier` node is
      // `required` and must be resubmitted on every POST. Omitting it makes
      // Kratos reject the request with 400 / "Property identifier is
      // missing." (id 4000002) and NO email is sent.
      setStep('authenticate');
      try {
        await loginMutation.mutateAsync({
          flow: flow!,
          values: {
            method: 'code',
            identifier: email,
            csrf_token: getCsrfToken(),
          },
        });
      } catch (error) {
        console.error('Code method submission error:', error);
      } finally {
        // Kratos returns HTTP 400 with the updated `sent_email` flow (the
        // OTP entry step) even on success, so the mutation resolves without
        // a session. Refetch to pull that flow and render the code form.
        refetch();
      }
    } else if (methodId === 'passkey') {
      // For passkey, execute immediately and show result
      setStep('authenticate');
      try {
        console.log('Passkey method selected, attempting to trigger WebAuthn...');
        
        // Wait a moment for the DOM to be ready and script to be loaded
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check what functions are available on window
        console.log('Available window functions:', Object.keys(window).filter(key => key.includes('ory')));
        
        if (window.oryPasskeyLogin) {
          console.log('Using oryPasskeyLogin function');
          window.oryPasskeyLogin();
        } else {
          console.error('oryPasskeyLogin function not available');
          console.log('Window object keys containing "passkey":', Object.keys(window).filter(key => key.toLowerCase().includes('passkey')));
          
          // Try alternative approach - maybe the function has a different name
          if (typeof (window as any).oryPasskeyLoginAutocompleteInit === 'function') {
            console.log('Trying oryPasskeyLoginAutocompleteInit instead');
            (window as any).oryPasskeyLoginAutocompleteInit();
          } else {
            // Show error message to user
            console.error('Passkey authentication is not available');
            setStep('choose_method');
            setSelectedMethod('');
          }
        }
      } catch (error) {
        console.error('Passkey method error:', error);
        setStep('choose_method');
        setSelectedMethod('');
      }
    } else {
      // For password method, show the form
      setStep('authenticate');
    }
  };

  const handleChangeUser = () => {
    // "Change user" was a no-op because the step-decider useEffect re-reads
    // the current flow on every render — and that flow still carries the
    // submitted identifier, so it kept routing us back to `choose_method`.
    // Fix: drop the current flow entirely and ask Kratos for a fresh one.
    // That redirect lands on /auth/login?flow=<new-id> with no identifier
    // pre-filled, so the identify step actually shows.
    sessionStorage.removeItem('loginFlowBounce');
    const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
    window.location.href = `${kratosUrl}/self-service/login/browser`;
  };

  const handlePasswordSubmit = async (password: string) => {
    if (!flow) return;

    try {
      await loginMutation.mutateAsync({
        flow: flow,
        values: {
          method: 'password',
          identifier: email,
          password: password,
          csrf_token: getCsrfToken(),
        },
      });
    } catch (error) {
      // Error is handled by the mutation
      // Refetch the flow to get updated error messages
      refetch();
    }
  };

  /**
   * Verify the email sign-in code (OTP). Kratos expects
   * `{ method:'code', identifier, code, csrf_token }`. On the correct code
   * the login mutation returns a session and navigates to /dashboard; on a
   * wrong/expired code Kratos returns the flow with an inline error, so we
   * refetch to surface it in the code form.
   */
  const handleCodeSubmit = async (code: string) => {
    if (!flow) return;
    try {
      const result = await loginMutation.mutateAsync({
        flow,
        values: {
          method: 'code',
          identifier: email,
          code,
          csrf_token: getCsrfToken(),
        },
      });
      // Session present → onSuccess already navigated; the flow is consumed,
      // so don't refetch it (that would 410). Otherwise pull the updated
      // flow to show the "invalid code" error.
      if (!result?.session) refetch();
    } catch (error) {
      console.error('Code verification error:', error);
      refetch();
    }
  };

  /** Ask Kratos to re-send the sign-in code (name `resend`, value `code`). */
  const handleResendCode = async () => {
    if (!flow) return;
    try {
      await loginMutation.mutateAsync({
        flow,
        values: {
          method: 'code',
          identifier: email,
          resend: 'code',
          csrf_token: getCsrfToken(),
        },
      });
    } catch (error) {
      console.error('Resend code error:', error);
    } finally {
      refetch();
    }
  };

  /**
   * Generic submit for AAL2 step-up methods (TOTP, lookup_secret).
   * Kratos expects `{method, <fieldName>, csrf_token}` — no `identifier`,
   * the identity is fixed by the AAL1 session cookie.
   *
   * On success, the login mutation already navigates to /dashboard (the
   * session response carries aal2). On 400, we refetch so the inline
   * error message in the relevant node is shown by `Aal2CodeForm`.
   */
  const handleAal2Submit = async (
    method: Aal2Method,
    fieldName: Aal2FieldName,
    value: string,
  ) => {
    if (!flow) return;
    try {
      await loginMutation.mutateAsync({
        flow,
        values: {
          method,
          [fieldName]: value,
          csrf_token: getCsrfToken(),
        } as unknown as LoginValues,
      });
    } catch (err) {
      refetch();
    }
  };

  /**
   * Memo so the step-decider useEffect and the JSX share the same source
   * of truth for whether the current flow is an AAL2 step-up.
   */
  const isAal2 = React.useMemo(
    () => (flow as any)?.requested_aal === 'aal2',
    [flow],
  );

  /**
   * AAL2 factors the user has enrolled, derived from the flow nodes.
   * Used to (a) seed the default selectedMethod in the step-decider and
   * (b) render the "use another method" toggle link below the input form.
   * Empty array for non-AAL2 flows.
   */
  const aal2Groups = React.useMemo((): string[] => {
    if (!flow || !isAal2) return [];
    const groups = flow.ui.nodes
      .filter(
        (n) =>
          n.type === 'input' &&
          ['button', 'submit'].includes(n.attributes.type as string) &&
          n.attributes.value !== 'identifier_first',
      )
      .map((n) => n.group);
    return Array.from(new Set(groups));
  }, [flow, isAal2]);

  /**
   * Pulls the error message Kratos surfaces after a failed AAL2 submit.
   *
   * Kratos puts these errors in two possible places:
   *
   * 1. **`flow.ui.messages`** (global) — this is what we get for the
   *    common case of "The provided authentication code is invalid, please
   *    try again." (id 4000008). Empirically this is where it lands for
   *    TOTP / lookup_secret failures on aal=aal2 flows.
   *
   * 2. **`flow.ui.nodes[<input>].messages`** — per-node, used for
   *    field-level validation errors (e.g. "Property totp_code is
   *    missing." when the field is absent from the body).
   *
   * We check the global first since that's where the wrong-code message
   * comes through, then fall back to a per-node lookup for the
   * field-level errors. Filtering by `group === method` keeps the lookup
   * scoped (we don't want to show a password error on a TOTP form).
   */
  const getMethodError = (method: string): string | undefined => {
    if (!flow) return undefined;

    // Global flow error — the common case for AAL2 failures.
    const globalErr = (flow.ui.messages as any)?.find(
      (m: any) => m.type === 'error',
    );
    if (globalErr?.text) return globalErr.text;

    // Per-node error — fall back when Kratos validates the field itself.
    const node = flow.ui.nodes.find(
      (n) =>
        n.group === method &&
        (n.messages as any)?.some((m: any) => m.type === 'error'),
    );
    const msg = (node?.messages as any)?.find((m: any) => m.type === 'error');
    return msg?.text;
  };

  /** Sign out the AAL1 partial session and restart the login flow clean. */
  const handleSignOutFromStepUp = async () => {
    try {
      const lf = await createLogoutFlow();
      await performLogout(lf.logout_token);
    } catch (err) {
      console.error('Sign out from step-up failed:', err);
    }
    clearSession();
    useSettingsStore.getState().clearAllContext();
    sessionStorage.removeItem('loginFlowBounce');
    window.location.href = '/auth/login';
  };

  /** When the flow is expired, clean state and ask Kratos for a fresh one. */
  const handleStartOver = () => {
    sessionStorage.removeItem('loginFlowBounce');
    const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
    window.location.href = `${kratosUrl}/self-service/login/browser`;
  };

  const handleKratosFormSubmit = async (formData: FormData) => {
    if (!flow) return;

    console.log('Handling Kratos form submission...');
    
    // Convert FormData to object
    const values: Record<string, unknown> = {};
    for (const [key, value] of formData.entries()) {
      values[key] = String(value);
    }

    // Ensure we have a method - for passkey submissions, it should be 'passkey'
    if (!values.method) {
      values.method = selectedMethod || 'passkey';
    }

    console.log('Form values:', values);

    try {
      await loginMutation.mutateAsync({
        flow: flow,
        values: values as LoginValues,
      });
    } catch (error) {
      console.error('Kratos form submission error:', error);
      // Refetch the flow to get updated error messages
      refetch();
    }
  };

  const getCsrfToken = (): string => {
    if (!flow) {
      console.log('No flow available for CSRF token');
      return '';
    }
    const csrfNode = flow.ui.nodes.find(node => 
      node.attributes.name === 'csrf_token'
    );
    const token = String(csrfNode?.attributes.value || '');
    console.log('CSRF token extracted:', token ? 'Found' : 'Not found', csrfNode);
    return token;
  };

  // Show loading while checking session or redirecting
  if (
    redirectingToSso ||
    checkingSession ||
    !showUi ||
    !flowId ||
    isLoading
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-8">
            <div
              className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
              aria-hidden="true"
            />
            <div className="text-sm text-muted-foreground">
              {redirectingToSso
                ? 'Redirecting to your identity provider…'
                : checkingSession
                ? 'Checking session...'
                : !flowId
                ? 'Redirecting to login...'
                : 'Loading...'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    // The flow is gone for good (back button on a consumed flow, expired
    // privileged session, etc.). Stop trying to refetch — it's never going
    // to load — and hand the user a clean restart instead of looping
    // between Try Again and Kratos.
    if (flowExpired) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Your login link expired</CardTitle>
              <CardDescription>
                Start a fresh sign-in flow to continue.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleStartOver} className="w-full">
                Start over
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>Failed to load login form</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => refetch()} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign In</CardTitle>
          <CardDescription>
            Welcome back! Please sign in to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Render Kratos form - required for passkey authentication */}
          {flow && (
            <form 
              id="kratos-form"
              action={flow.ui.action}
              method={flow.ui.method}
              style={{ display: 'none' }}
              onSubmit={(e) => {
                e.preventDefault();
                console.log('Kratos form submitted - handling via React');
                // Handle form submission via our React logic
                handleKratosFormSubmit(new FormData(e.currentTarget));
              }}
            >
              {flow.ui.nodes
                .filter(node => node.type === 'input')
                .map((node, index) => {
                  const attrs = node.attributes;
                  return (
                    <input
                      key={index}
                      type={attrs.type as string}
                      name={attrs.name as string}
                      value={String(attrs.value || '')}
                      disabled={attrs.disabled as boolean}
                      required={attrs.required as boolean}
                      readOnly={true}
                      style={{ display: attrs.type === 'hidden' ? 'none' : 'block' }}
                    />
                  );
                })}
            </form>
          )}

          {/* Security-aware error messages */}
          {(showGenericError || (flow?.ui.messages && flow.ui.messages.length > 0)) && (
            <div className="space-y-2">
              {/* Generic error for security (prevents user enumeration) */}
              {showGenericError && (
                <div className="p-3 text-sm rounded-md text-destructive bg-destructive/10 border border-destructive/20">
                  <div>Invalid email or password. Please check your credentials and try again.</div>
                  <div className="mt-2 pt-2 border-t border-destructive/20">
                    <p className="text-xs text-muted-foreground">
                      Don't have an account?{' '}
                      <Link 
                        to="/auth/registration" 
                        className="text-primary hover:underline font-medium"
                      >
                        Create one here
                      </Link>
                    </p>
                  </div>
                </div>
              )}
              
              {/* Kratos messages (filtered for security) */}
              {flow?.ui.messages && flow.ui.messages
                .filter(message => {
                  // Filter out account enumeration errors for security
                  if (message.id === 4000037) {
                    // "This account does not exist or has no login method configured"
                    // Don't show this to prevent user enumeration
                    return false;
                  }
                  return true;
                })
                .map((message, index) => (
                  <div 
                    key={index}
                    className={`p-3 text-sm rounded-md ${
                      message.type === 'error' 
                        ? 'text-destructive bg-destructive/10 border border-destructive/20'
                        : message.type === 'success'
                        ? 'text-green-700 bg-green-50 border border-green-200'
                        : 'text-blue-700 bg-blue-50 border border-blue-200'
                    }`}
                  >
                    {message.text}
                  </div>
                ))}
            </div>
          )}

          {/* Step 1: Identifier Input */}
          {step === 'identify' && (
            <>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Enter your email address to get started
                </p>
                <IdentifierInput
                  value=""
                  onChange={() => {
                    // Clear generic error when user starts typing
                    if (showGenericError) {
                      setShowGenericError(false);
                    }
                  }}
                  onSubmit={handleIdentifierSubmit}
                  disabled={loginMutation.isPending}
                  placeholder="Enter your email"
                />
              </div>
            </>
          )}

          {/* Step 2: Method Selection */}
          {step === 'choose_method' && (
            <>
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Signed in as: <span className="font-medium">{email}</span>
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleChangeUser}
                    className="text-xs"
                  >
                    Change user
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm font-medium text-foreground">
                    Choose authentication method:
                  </div>
                  <div className="grid gap-2">
                    {availableMethods.map((method) => (
                      <Button
                        key={method.id}
                        variant="outline"
                        className="flex items-center justify-start space-x-3 p-4 h-auto"
                        onClick={() => handleMethodSelect(method.id)}
                        disabled={loginMutation.isPending}
                      >
                        <div className="flex-shrink-0">
                          {method.icon}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium">
                            {method.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {method.description}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Authentication Form */}
          {step === 'authenticate' && (
            <>
              <div className="space-y-4">
                <div className="text-center">
                  {/* Only show "Signed in as / Continuing as" when we
                      actually have an email to show — in some AAL2 flows
                      the identity traits arrive on the second tick and
                      the bare label looked broken. */}
                  {email && (
                    <p className="text-sm text-muted-foreground">
                      {isAal2 ? 'Signed in as:' : 'Continuing as:'}{' '}
                      <span className="font-medium">{email}</span>
                    </p>
                  )}
                  {isAal2 ? (
                    // In an AAL2 step-up the identity is fixed by the
                    // existing cookie — letting the user "Change user"
                    // would break the flow. We give them a real sign-out
                    // path instead so they can start over clean.
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSignOutFromStepUp}
                      className="text-xs"
                    >
                      Not you? Sign out
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleChangeUser}
                      className="text-xs"
                    >
                      Change user
                    </Button>
                  )}
                </div>

                {/* Password form */}
                {selectedMethod === 'password' && (
                  <PasswordForm
                    onSubmit={handlePasswordSubmit}
                    disabled={loginMutation.isPending}
                  />
                )}

                {/* TOTP code (AAL2 step-up) */}
                {selectedMethod === 'totp' && (
                  <Aal2CodeForm
                    method="totp"
                    fieldName="totp_code"
                    label={t('auth.mfaTotpStepUpHeading')}
                    description={t('auth.mfaTotpStepUpDescription')}
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    disabled={loginMutation.isPending}
                    error={getMethodError('totp')}
                    onSubmit={handleAal2Submit}
                  />
                )}

                {/* Backup / lookup_secret code (AAL2 step-up fallback) */}
                {selectedMethod === 'lookup_secret' && (
                  <Aal2CodeForm
                    method="lookup_secret"
                    fieldName="lookup_secret"
                    label={t('auth.mfaBackupCodesStepUpHeading')}
                    description={t('auth.mfaBackupCodesStepUpDescription')}
                    placeholder={t('auth.mfaBackupCodesCodePlaceholder')}
                    inputMode="text"
                    disabled={loginMutation.isPending}
                    error={getMethodError('lookup_secret')}
                    onSubmit={handleAal2Submit}
                  />
                )}

                {/* AAL2 alt-method toggle — small footer link, only when
                    the user has more than one factor enrolled. We hide it
                    entirely if only one method is available so the form
                    stays minimal. The toggle just swaps `selectedMethod`;
                    the step-decider effect honours the manual choice. */}
                {isAal2 &&
                  (selectedMethod === 'totp' || selectedMethod === 'lookup_secret') &&
                  aal2Groups.includes(
                    selectedMethod === 'totp' ? 'lookup_secret' : 'totp',
                  ) && (
                    <div className="text-center pt-2">
                      <Button
                        variant="link"
                        size="sm"
                        type="button"
                        onClick={() =>
                          setSelectedMethod(
                            selectedMethod === 'totp' ? 'lookup_secret' : 'totp',
                          )
                        }
                        className="text-xs text-muted-foreground h-auto p-0"
                      >
                        {selectedMethod === 'totp'
                          ? t('auth.mfaUseBackupCodeInstead')
                          : t('auth.mfaUseAuthenticatorInstead')}
                      </Button>
                    </div>
                  )}

                {/* Email-code login. Once Kratos exposes the `code` text
                    node (sent_email state) we render the OTP entry form;
                    until then we show a lightweight "sending" note. */}
                {selectedMethod === 'code' &&
                  (flow.ui.nodes.some(
                    (n) =>
                      n.attributes.name === 'code' &&
                      n.attributes.type !== 'hidden' &&
                      n.attributes.type !== 'submit'
                  ) ? (
                    <CodeForm
                      email={email}
                      onSubmit={handleCodeSubmit}
                      onResend={handleResendCode}
                      disabled={loginMutation.isPending}
                      error={getMethodError('code')}
                    />
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-2">
                      Sending sign-in code…
                    </div>
                  ))}

                {selectedMethod === 'passkey' && (
                  <div className="text-center space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-700 font-medium">
                        Passkey authentication initiated
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Please follow your browser's prompts to complete authentication
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setStep('choose_method')}
                      className="text-sm"
                    >
                      Try another method
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Links to other auth pages - only show in identify step */}
          {step === 'identify' && (
            <div className="text-center space-y-2">
              <div className="text-sm">
                <Link 
                  to="/auth/recovery" 
                  className="text-primary hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
              <div className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link 
                  to="/auth/registration" 
                  className="text-primary hover:underline"
                >
                  Sign up
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
